/**
 * Lottie Play — loads a JSON Lottie file from `public/lottie/` and renders it
 * with optional headline / subline. Falls back to an animated burst if lottie-web
 * is unavailable in the browser context.
 */
import type { SceneBuilder } from "../../reel/scenes.js";
import { brandBackdrop } from "../../primitives/brandBackdrop.js";
import {
  accentFor, clipOutroFade, combineSceneFragments, escapeHtml, makeTl,
  typeSize, weights, withAlpha, colors as themeColors, gsapSetLine,
} from "../../reel/sceneShared.js";

export const lottiePlay: SceneBuilder = ({ ctx, clipPrefix, inputs, clipDurationSec, clipStartSec }) => {
  const accent = accentFor(inputs);
  const text = inputs.text ?? "";
  const sub = inputs.subtext ?? "";
  const file = String(inputs.extras?.file ?? "demo.json").replace(/^\/+/, "");
  const sizePct = Math.max(20, Math.min(95, Number(inputs.extras?.size ?? 65)));

  const lg = typeSize("lg", ctx.format);
  const sm = typeSize("sm", ctx.format);

  const wrapId = `${clipPrefix}-wrap`;
  const titleId = `${clipPrefix}-title`;
  const subId = `${clipPrefix}-sub`;
  const stageId = `${clipPrefix}-stage`;
  const lottieId = `${clipPrefix}-lottie`;
  const haloId = `${clipPrefix}-halo`;

  const t = makeTl(clipStartSec);
  const backdrop = brandBackdrop(ctx, `${clipPrefix}-bg`, { variant: "cinematic", accent });

  const lottiePath = `assets/lottie/${file}`;
  const sizePx = `${sizePct}%`;

  const html = `<div id="${wrapId}" class="clip" data-start="${clipStartSec.toFixed(4)}" data-duration="${clipDurationSec.toFixed(4)}" style="position:absolute;inset:0;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:32px;padding:80px;">
    ${text ? `<div id="${titleId}" style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${lg * 0.78}px;letter-spacing:-2px;color:${themeColors.fg};line-height:1.02;text-align:center;text-wrap:balance;max-width:1080px;">${escapeHtml(text)}</div>` : ""}
    <div id="${stageId}" style="position:relative;width:${sizePx};height:${sizePx};max-width:90%;max-height:60vh;display:flex;align-items:center;justify-content:center;">
      <div id="${haloId}" style="position:absolute;inset:-30px;border-radius:50%;background:radial-gradient(closest-side, ${withAlpha(accent, 0.5)} 0%, transparent 70%);filter:blur(30px);"></div>
      <div id="${lottieId}" data-lottie-src="${escapeHtml(lottiePath)}" style="position:relative;width:100%;height:100%;color:${accent};display:flex;align-items:center;justify-content:center;">
        <!-- Fallback geometric pulse if lottie-web isn't loaded; the runtime script attempts to swap this with the real animation. -->
        <svg viewBox="0 0 100 100" style="width:100%;height:100%;">
          <circle cx="50" cy="50" r="22" fill="none" stroke="${accent}" stroke-width="3" opacity="0.6"/>
          <circle cx="50" cy="50" r="14" fill="${accent}" opacity="0.85"/>
        </svg>
      </div>
    </div>
    ${sub ? `<div id="${subId}" style="font-family:'Inter',sans-serif;font-weight:${weights.semibold};font-size:${sm * 0.9}px;color:${withAlpha(themeColors.fg, 0.78)};text-align:center;max-width:760px;text-wrap:balance;">${escapeHtml(sub)}</div>` : ""}
  </div>`;

  const init = [
    text ? gsapSetLine(`#${titleId}`, { opacity: 0, y: 16 }) : "",
    sub ? gsapSetLine(`#${subId}`, { opacity: 0, y: 12 }) : "",
    gsapSetLine(`#${stageId}`, { opacity: 0, scale: 0.85 }),
    gsapSetLine(`#${haloId}`, { opacity: 0, scale: 0.7 }),
  ].filter(Boolean).join("\n");

  const tweens: string[] = [
    t.to(`#${stageId}`, { opacity: 1, scale: 1, duration: 0.85, ease: "raw:\"back.out(1.5)\"" }, 0.05),
    t.to(`#${haloId}`, { opacity: 0.85, scale: 1, duration: 0.9, ease: "raw:\"power2.out\"" }, 0.1),
    t.to(`#${haloId}`, { opacity: 0.5, scale: 1.05, duration: 1.2, repeat: -1, yoyo: true, ease: "raw:\"sine.inOut\"" }, 1.0),
    `tl.add(function(){try{if(window.lottie){var el=document.getElementById(${JSON.stringify(lottieId)});if(el && !el.dataset.loaded){el.dataset.loaded="1";el.innerHTML="";window.lottie.loadAnimation({container:el,renderer:"svg",loop:true,autoplay:true,path:el.getAttribute("data-lottie-src")});}}}catch(e){}}, ${(clipStartSec + 0.1).toFixed(4)});`,
  ];
  if (text) tweens.push(t.to(`#${titleId}`, { opacity: 1, y: 0, duration: 0.55, ease: "raw:\"power2.out\"" }, 0.2));
  if (sub) tweens.push(t.to(`#${subId}`, { opacity: 1, y: 0, duration: 0.55, ease: "raw:\"power2.out\"" }, 0.5));
  tweens.push(clipOutroFade(clipPrefix, clipDurationSec, clipStartSec));

  return combineSceneFragments([backdrop, { html, init, tweens }]);
};
